import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Popover } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { HexColorPicker } from 'react-colorful';
import CheckIcon from '@material-ui/icons/Check';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { TEXT, SPACE } from '../../shared_components';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { createTemplateStageApi, updateStageApi } from '../apiService';
import { STAGE_TYPE_OPTIONS, STAGE_ACCESS_OPTIONS } from '../constants';

const errorMessage = (response) => response?.data?.message || 'Unable to save the stage due to a technical error.';

const DEFAULT_COLOUR_HEX = '#1677FF';

const PRESET_HEX_COLOURS = ['#1677FF', '#12B76A', '#FF7900', '#673DE6', '#f85149', '#8b949e'];

const HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/* Legacy preset ids from before the free-form colour picker, kept only to render
   stages saved under the old contract. */
const LEGACY_PRESET_HEX = {
  blue: '#1677FF', green: '#12B76A', orange: '#FF7900', purple: '#673DE6', red: '#f85149', grey: '#8b949e',
};

const resolveStageColourHex = (stageColour) => {
  if (HEX_PATTERN.test(stageColour)) return stageColour.toUpperCase();
  return (LEGACY_PRESET_HEX[stageColour] || DEFAULT_COLOUR_HEX).toUpperCase();
};

const OptionField = ({ label, options, value, onChange, fullWidth = true }) => (
  <Autocomplete
    options={options}
    disableClearable
    fullWidth={fullWidth}
    getOptionLabel={(option) => option.label}
    getOptionSelected={(option, current) => option.value === current.value}
    value={options.find((option) => option.value === value) || null}
    onChange={(event, newValue) => onChange(newValue.value)}
    renderInput={(params) => (
      <TextField {...params} label={label} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
    )}
  />
);

const Swatch = ({ hex, selected, size = 26, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyPress={(event) => (event.key === 'Enter' ? onClick() : null)}
    style={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: '50%',
      backgroundColor: hex,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${hex}` : '0 0 0 1px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.15s ease',
    }}
  >
    {selected ? <CheckIcon style={{ fontSize: size * 0.55, color: '#fff' }} /> : null}
  </div>
);

const ColourPicker = ({ value, onChange, stageName }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hexInput, setHexInput] = useState(value || DEFAULT_COLOUR_HEX);
  const selectedHex = HEX_PATTERN.test(value) ? value : DEFAULT_COLOUR_HEX;

  const openPicker = (event) => {
    setHexInput(selectedHex);
    setAnchorEl(event.currentTarget);
  };

  const commitHex = (hex) => {
    if (HEX_PATTERN.test(hex)) {
      onChange(hex.toUpperCase());
    }
  };

  const handleHexInputChange = (event) => {
    const next = event.target.value.startsWith('#') ? event.target.value : `#${event.target.value}`;
    setHexInput(next);
    commitHex(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
      <Typography variant="caption" style={{ color: TEXT.secondary }}>Colour</Typography>
      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 40,
          padding: '0 10px',
          border: '1px solid rgba(0,0,0,0.23)',
          borderRadius: 4,
          cursor: 'pointer',
        }}
      >
        <Swatch hex={selectedHex} size={20} onClick={() => {}} />
        <Typography variant="body2">{selectedHex.toUpperCase()}</Typography>
        <ArrowDropDownIcon style={{ marginLeft: 'auto', color: TEXT.secondary }} />
      </div>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div style={{ padding: 16, width: 240, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>Choose a colour</Typography>
          <HexColorPicker color={selectedHex} onChange={(hex) => { setHexInput(hex); commitHex(hex); }} style={{ width: '100%' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Swatch hex={selectedHex} size={22} onClick={() => {}} />
            <TextField
              value={hexInput}
              onChange={handleHexInputChange}
              variant="outlined"
              size="small"
              fullWidth
              error={!!hexInput && !HEX_PATTERN.test(hexInput)}
            />
          </div>
          <div>
            <Typography variant="caption" style={{ color: TEXT.secondary, display: 'block', marginBottom: 6 }}>Preset colours</Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {PRESET_HEX_COLOURS.map((hex) => (
                <Swatch
                  key={hex}
                  hex={hex}
                  size={26}
                  selected={selectedHex.toLowerCase() === hex.toLowerCase()}
                  onClick={() => {
                    setHexInput(hex);
                    commitHex(hex);
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #EEE', paddingTop: 10 }}>
            <Typography variant="caption" style={{ color: TEXT.secondary, display: 'block', marginBottom: 6 }}>Preview</Typography>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8,
              border: `1px solid ${selectedHex}`, backgroundColor: `${selectedHex}14`,
            }}
            >
              <Swatch hex={selectedHex} size={22} selected onClick={() => {}} />
              <Typography variant="body2" style={{ fontWeight: 600 }}>{stageName?.trim() || 'Stage name'}</Typography>
            </div>
          </div>
        </div>
      </Popover>
    </div>
  );
};

const StageFormDialog = ({ templateId, addEditStage, onToggleModal, getResponseBack, enqueueSnackbar }) => {
  const isEdit = addEditStage?.mode === 'edit';
  const stage = addEditStage?.data;
  const [name, setName] = useState(isEdit ? (stage?.name || '') : '');
  const [description, setDescription] = useState(isEdit ? (stage?.description || '') : '');
  const [colour, setColour] = useState(isEdit ? resolveStageColourHex(stage?.colour) : DEFAULT_COLOUR_HEX);
  const [stageType, setStageType] = useState(isEdit ? (stage?.stage_type?.id || 'standard') : 'standard');
  const [entryAccess, setEntryAccess] = useState(isEdit ? (stage?.entry_access?.id || 'any') : 'any');
  const [exitAccess, setExitAccess] = useState(isEdit ? (stage?.exit_access?.id || 'any') : 'any');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({});

  const handleSave = () => {
    const validationInputs = {
      name: name?.trim() ? '' : 'Please enter Stage Name',
    };
    if (!Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
      setError(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Stage Failed',
        page_title: 'Planner Tracker Add/Update Stage',
        template_id: templateId || '',
        stage_id: stage?.id || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
      return;
    }

    setSaving(true);

    const payload = {
      name: name?.trim(),
      description: description?.trim() || '',
      colour,
      stage_type: stageType,
      entry_access: entryAccess,
      exit_access: exitAccess,
    };

    const request = isEdit
      ? globalPutService(updateStageApi(stage?.id), payload)
      : globalPostService(createTemplateStageApi(templateId), payload);

    request.then((response) => {
      setSaving(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(isEdit ? 'Stage updated' : 'Stage created', { variant: 'success' });
        getResponseBack();
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Stage Success`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Stage`,
          template_id: templateId || '',
          stage_id: stage?.id || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Stage Failed`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Stage`,
          template_id: templateId || '',
          stage_id: stage?.id || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <Dialog open={!!addEditStage?.modal} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{isEdit ? 'Edit Stage' : 'New Stage'}</Typography>
        {!isEdit ? (
          <Typography variant="body2" style={{ color: TEXT.secondary, marginTop: 2 }}>
            Added to this template — every later stage's order shifts automatically.
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          <TextField
            label="Stage Name"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 255 }}
            value={name}
            onChange={(event) => setName(event.target.value.trimStart())}
            onFocus={() => setError((prev) => (prev.name ? { ...prev, name: '' } : prev))}
            required
            autoFocus
            fullWidth
            error={!!error.name}
            helperText={error.name || ''}
          />
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: 500 }}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'flex-end' }}>
            <ColourPicker value={colour} onChange={setColour} stageName={name} />
            <div style={{ flex: 1 }}>
              <OptionField label="Stage Type" options={STAGE_TYPE_OPTIONS} value={stageType} onChange={setStageType} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: SPACE.md }}>
            <OptionField label="Entry Access" options={STAGE_ACCESS_OPTIONS} value={entryAccess} onChange={setEntryAccess} />
            <OptionField label="Exit Access" options={STAGE_ACCESS_OPTIONS} value={exitAccess} onChange={setExitAccess} />
          </div>
        </div>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={() => onToggleModal()}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Stage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StageFormDialog;
